import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../services/apiClient';
import { WizardComponentProps, EntityMappingV2, MappingStatus } from '../../types';

const ValidationPanelV3: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();

  const { data: mappings, isLoading } = useQuery({
    queryKey: ['entityMappingsV2', customerId, phaseId],
    queryFn: () => apiClient.getEntityMappingsV2(customerId!, phaseId!),
    enabled: !!(customerId && phaseId)
  });

  const entities = Array.isArray(mappings) ? mappings as EntityMappingV2[] : [];

  const stats = {
    total: entities.length,
    complete: entities.filter(e => e.status === MappingStatus.COMPLETE || e.status === MappingStatus.LOCKED).length,
    inProgress: entities.filter(e => e.status === MappingStatus.IN_PROGRESS).length,
    draft: entities.filter(e => e.status === MappingStatus.DRAFT).length,
    validated: entities.filter(e => e.status === MappingStatus.VALIDATED).length,
  };

  const getStatusColor = (status: MappingStatus) => {
    switch (status) {
      case MappingStatus.COMPLETE:
      case MappingStatus.LOCKED:
      case MappingStatus.VALIDATED:
        return 'text-green-600 bg-green-50 border-green-200';
      case MappingStatus.IN_PROGRESS:
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: MappingStatus) => {
    switch (status) {
      case MappingStatus.COMPLETE:
      case MappingStatus.LOCKED:
      case MappingStatus.VALIDATED:
        return <CheckCircleIcon className="w-5 h-5" />;
      case MappingStatus.IN_PROGRESS:
        return <ClockIcon className="w-5 h-5" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {!isEmbedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Validation Status (V3)</h1>
          <p className="text-gray-600 mt-2">Overview of mapping validation status for all entities</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Entities</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
            <div className="text-3xl font-bold text-green-600">{stats.complete}</div>
            <div className="text-sm text-gray-500">Completed</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
            <div className="text-3xl font-bold text-blue-600">{stats.inProgress}</div>
            <div className="text-sm text-gray-500">In Progress</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
            <div className="text-3xl font-bold text-gray-600">{stats.draft}</div>
            <div className="text-sm text-gray-500">Draft</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Entity Status Summary</h3>
        </div>
        {isLoading ? (
             <div className="p-8 text-center text-gray-500">Loading mappings...</div>
        ) : entities.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No entities mapped yet.</div>
        ) : (
            <ul className="divide-y divide-gray-200">
                {entities.map(entity => (
                    <li key={entity.entityName} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                        <div>
                            <span className="font-medium text-gray-900">{entity.entityName}</span>
                            <div className="text-sm text-gray-500">
                                {entity.fieldMappings?.length || 0} fields mapped
                            </div>
                        </div>
                        <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(entity.status)}`}>
                            <span className="mr-2">{getStatusIcon(entity.status)}</span>
                            <span className="capitalize">{entity.status.replace('_', ' ')}</span>
                        </div>
                    </li>
                ))}
            </ul>
        )}
      </div>

      {stats.complete < stats.total && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
              <div>
                  <h4 className="text-sm font-medium text-yellow-800">Migration Not Ready</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                      Please complete mapping finalization for all entities in the Mapping step before proceeding to export.
                  </p>
              </div>
          </div>
      )}
    </div>
  );
};

export default ValidationPanelV3;
